<template>
    <div class="setup-container">
        <div class="page-hero">
            <div>
                <p class="eyebrow">Permissions</p>
                <h1>Assign Role</h1>
                <p class="hero-copy">Attach roles to members and see existing assignments below.</p>
            </div>
            <button class="setup-btn" @click="loadData">
                <RefreshCcw class="icon" />
                Refresh
            </button>
        </div>

        <section class="panel">
            <form class="setup-form" @submit.prevent="submitForm">
                <div class="form-group">
                    <v-autocomplete
                        :model-value="form.memberId"
                        @update:modelValue="setMemberValue"
                        :items="memberItems"
                        item-title="title"
                        item-value="value"
                        density="compact"
                        label="Member"
                        variant="outlined"
                        clearable
                        :return-object="false"
                    />
                </div>

                <div class="form-group">
                    <label for="roleId">Role</label>
                    <select id="roleId" v-model="form.roleId" required>
                        <option value="" disabled>Select role</option>
                        <option v-for="role in roles" :key="role.RoleId" :value="role.RoleId">
                            {{ role.RoleName }}
                        </option>
                    </select>
                </div>

                <div class="buttons mt-3">
                    <button type="submit">Assign Role</button> &nbsp;
                    <button type="button" @click="resetForm">Reset</button>
                </div>
            </form>
        </section>

        <section class="panel">
            <div class="header-search">
                <div>
                    <p class="section-kicker">Assignments</p>
                    <h2>Member roles</h2>
                </div>
                <div class="search-bar">
                    <Search />
                    <input v-model="searchQuery" type="text" placeholder="Search assignments..." />
                </div>
            </div>

            <table class="table">
                <thead>
                    <tr>
                        <th>#</th>
                        <th>User</th>
                        <th>Role</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-if="paginatedAssignments.length === 0">
                        <td colspan="3" class="no-data">No assignments found.</td>
                    </tr>
                    <tr v-else v-for="(item, index) in paginatedAssignments" :key="`${item.MemberId}-${item.RoleId}`">
                        <td>{{ (currentPage - 1) * pageSize + index + 1 }}</td>
                        <td>{{ item.lastname }}, {{ item.firstname }} {{ item.othernames }}({{ item.membership_number }})</td>
                        <td>{{ item.RoleName || `Role #${item.RoleId}` }}</td>
                    </tr>
                </tbody>
            </table>

            <div class="pagination">
                <button @click="prevPage" :disabled="currentPage === 1">
                    <ChevronLeft />
                </button>
                <button
                    v-for="page in totalPages"
                    :key="page"
                    @click="goToPage(page)"
                    :class="{ active: currentPage === page }"
                >
                    {{ page }}
                </button>
                <button @click="nextPage" :disabled="currentPage === totalPages">
                    <ChevronRight />
                </button>
            </div>
        </section>
    </div>
</template>

<script setup>
import { ChevronLeft, ChevronRight, RefreshCcw, Search } from "lucide-vue-next";
import { computed, onMounted, ref, watch } from "vue";
import axios from "axios";

const rolesApi = "/roles_lists.php";
const assignmentsApi = "/member_roles_lists.php";
const createApi = "/member_roles_setup.php";
const membersApi = "/members_lists.php";

const roles = ref([]);
const assignments = ref([]);
const members = ref([]);
const searchQuery = ref("");
const currentPage = ref(1);
const pageSize = 8;

const form = ref({
    memberId: "",
    roleId: ""
});

function formatMemberLabel(member) {
    const nameParts = [member.lastname, member.firstname, member.othernames]
        .filter(Boolean)
        .map((part) => String(part).trim());

    const fullName = nameParts.join(" ").trim();
    const memberNumber = member.membership_number ? `(${member.membership_number})` : "";

    return fullName ? `${fullName} ${memberNumber}`.trim() : `Member ${memberNumber}`.trim();
}

function setMemberValue(value) {
    if (value && typeof value === "object") {
        form.value.memberId = value.value ?? value.id ?? "";
        return;
    }

    form.value.memberId = value ?? "";
}

const memberItems = computed(() => {
    return members.value.map((member) => ({
        title: formatMemberLabel(member),
        value: member.userid
    }));
});

const filteredAssignments = computed(() => {
    const query = searchQuery.value.trim().toLowerCase();
    if (!query) return assignments.value;

    return assignments.value.filter((item) => {
        return (
            String(item.MemberId || "").toLowerCase().includes(query) ||
            String(item.RoleName || "").toLowerCase().includes(query) ||
            String(item.RoleId || "").toLowerCase().includes(query)
        );
    });
});

const totalPages = computed(() => Math.max(1, Math.ceil(filteredAssignments.value.length / pageSize)));

const paginatedAssignments = computed(() => {
    const start = (currentPage.value - 1) * pageSize;
    return filteredAssignments.value.slice(start, start + pageSize);
});

watch(searchQuery, () => {
    currentPage.value = 1;
});

function goToPage(page) {
    if (page >= 1 && page <= totalPages.value) currentPage.value = page;
}

function prevPage() {
    if (currentPage.value > 1) currentPage.value--;
}

function nextPage() {
    if (currentPage.value < totalPages.value) currentPage.value++;
}

function resetForm() {
    form.value.memberId = "";
    form.value.roleId = "";
}

async function loadData() {
    try {
        const [rolesResponse, assignmentsResponse, membersResponse] = await Promise.all([
            axios.get(rolesApi),
            axios.get(assignmentsApi),
            axios.get(membersApi)
        ]);

        roles.value = rolesResponse?.data?.data ?? [];
        assignments.value = assignmentsResponse?.data?.data ?? [];
        members.value = membersResponse?.data?.data ?? [];
    } catch (error) {
        console.error(error);
    }
}

async function submitForm() {
    try {
        const payload = new FormData();
        payload.append("MemberId", form.value.memberId);
        payload.append("RoleId", form.value.roleId);

        const response = await axios.post(createApi, payload, {
            headers: { "Content-Type": "multipart/form-data" }
        });

        alert(response?.data?.message || "Role assigned successfully.");
        resetForm();
        await loadData();
    } catch (error) {
        alert(error?.response?.data?.message || error.message);
    }
}

onMounted(loadData);
</script>
